import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, GeoPoint } from 'firebase/firestore';
import { db } from '../firebase';
import { MapContainer, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import HazardMarker from './HazardMarker';
import HazardDetailModal from './HazardDetailModal';
import { HazardAlert } from '../types';

const HazardMap: React.FC = () => {
  const [hazardAlerts, setHazardAlerts] = useState<HazardAlert[]>([]);
  const [selectedIsland, setSelectedIsland] = useState('All');
  const [selectedHazard, setSelectedHazard] = useState<HazardAlert | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track when hazard is selected
  const handleHazardSelect = (hazard: HazardAlert | null) => {
    console.log('🔍 Hazard selected:', hazard?.title || 'none');
    setSelectedHazard(hazard);
  };

  // Fix Leaflet default icon issue
  useEffect(() => {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, []);

  const islands = ['All', 'St. Kitts', 'Nevis'];

  // Fetch hazard alerts
  useEffect(() => {
    const hazardsQuery = query(
      collection(db, 'hazardAlerts'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribeHazards = onSnapshot(
      hazardsQuery,
      (snapshot) => {
        const hazardsData: HazardAlert[] = [];

        console.log(' Hazard snapshot received:', snapshot.size, 'documents');

        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log('🔍 Processing hazard document:', doc.id, data);
          
          // Handle coordinate - check if it's a GeoPoint or individual lat/lng fields
          let coordinate = { latitude: 0, longitude: 0 };
          
          if (data.coordinate instanceof GeoPoint) {
            // It's a GeoPoint object
            coordinate = {
              latitude: data.coordinate.latitude,
              longitude: data.coordinate.longitude
            };
          } else if (data.latitude && data.longitude) {
            // Individual lat/lng fields
            coordinate = {
              latitude: data.latitude,
              longitude: data.longitude
            };
          } else {
            console.warn('⚠️ No valid coordinates found for hazard:', doc.id);
          }
          
          const hazard: HazardAlert = {
            id: doc.id,
            title: data.title || '',
            description: data.description || '',
            hazardType: data.hazardType || 'other',
            coordinate: coordinate,
            island: data.island || 'St. Kitts',
            severity: data.severity || 'medium',
            isActive: data.isActive || true,
            createdAt: data.createdAt,
          };
          
          console.log('🔍 Processed hazard object:', hazard);
          hazardsData.push(hazard);
        });

        console.log(' Final hazards array:', hazardsData);
        setHazardAlerts(hazardsData);
        setIsLoading(false);
      },
      (err) => {
        console.error(' Error fetching hazard alerts:', err);
        setIsLoading(false);
      }
    );

    return () => unsubscribeHazards();
  }, []);

  // Filter hazards by island
  const filteredHazards = selectedIsland === 'All' 
    ? hazardAlerts 
    : hazardAlerts.filter(h => h.island === selectedIsland);
    
  console.log('🔍 Filtered hazards:', {
    selectedIsland,
    totalHazards: hazardAlerts.length,
    filteredCount: filteredHazards.length,
    filteredHazards: filteredHazards.map(h => ({ id: h.id, title: h.title, island: h.island, isActive: h.isActive }))
  });

  // Get hazard color
  const getHazardColor = (type: string) => {
    switch (type) {
      case 'roadwork': return '#FFA500';
      case 'accident': return '#FF0000';
      case 'flooding': return '#0000FF';
      case 'debris': return '#FFFF00';
      case 'closure': return '#800080';
      case 'construction': return '#8B4513';
      default: return '#808080';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Hazard Map</h1>
          <p className="text-gray-600 mt-1">
            View active hazards and road conditions in St. Kitts & Nevis
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Island Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Island
            </label>
            <select
              value={selectedIsland}
              onChange={(e) => setSelectedIsland(e.target.value)}
              className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {islands.map((island) => (
                <option key={island} value={island}>
                  {island}
                </option>
              ))}
            </select>
          </div>

          {/* Map */}
          <div className="relative h-96 rounded-lg overflow-hidden border border-gray-300 mb-4 z-10">
            <div className="absolute inset-0">
              <MapContainer
                center={[17.3079, -62.7266]}
                zoom={10}
                style={{ height: '100%', width: '100%', zIndex: 1 }}
                zoomControl={true}
                scrollWheelZoom={true}
                dragging={true}
                doubleClickZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {filteredHazards.map((hazard) => (
                  <HazardMarker
                    key={hazard.id}
                    hazard={hazard}
                    onMarkerClick={handleHazardSelect}
                  />
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Hazard Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">
                Active Hazards: {filteredHazards.length}
              </h3>
              <div className="space-y-1">
                {['roadwork', 'accident', 'flooding', 'debris', 'closure', 'construction'].map((type) => {
                  const count = filteredHazards.filter(h => h.hazardType === type).length;
                  if (count === 0) return null;
                  return (
                    <div key={type} className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getHazardColor(type) }}
                      ></div>
                      <span className="text-sm text-gray-600">
                        {type.charAt(0).toUpperCase() + type.slice(1)}: {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hazard Detail Modal */}
      {selectedHazard && (
        <HazardDetailModal
          hazard={selectedHazard}
          isOpen={!!selectedHazard}
          onClose={() => handleHazardSelect(null)}
        />
      )}
    </div>
  );
};

export default HazardMap;
